import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import AsyncLoader from '~components/AsyncLoader';
import { DotLoader } from '~components/DotLoader';
import EmptyState from '~components/EmptyState';
import useDebounce from '~hooks/useDebounce';
import CampaignGroupCard from '~pages/CampaignManagement/CampaignDetails/CampaignGroupList/CampaignGroupCard';
import useCampaignGroupSearch from '~pages/CampaignManagement/CampaignDetails/CampaignGroupList/useCampaignGroupSearch';

export const CampaignGroupList = () => {
  const { campaignId } = useParams() as { campaignId: string };
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const {
    loading,
    error,
    list,
    hasMore,
    intersectionObserverRef: lastDataElement,
  } = useCampaignGroupSearch(+campaignId, debouncedSearch);

  // If any filter property is set and displayList is empty we assume no relevant search results
  const noSearchResults = search && list.length === 0;

  const displayList = useMemo(
    () =>
      list.map((item, index) => {
        return (
          <CampaignGroupCard
            key={item.id}
            ref={index === list.length - 1 ? lastDataElement : null}
            id={item.id}
            name={item.name}
            isActive={item.isActive}
            isArchived={Boolean(item.archived)}
          />
        );
      }),

    [list, lastDataElement],
  );

  return (
    <>
      <Grid sx={{ marginBottom: 1 }} container spacing={1} alignContent='center'>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            variant='outlined'
            label='Search'
            id='search'
            name='search'
            defaultValue={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </Grid>

        <Hidden smDown>
          <Grid item md={9}></Grid>
        </Hidden>
      </Grid>

      <AsyncLoader
        isLoading={(loading && list.length === 0) || search !== debouncedSearch}
        error={
          !noSearchResults &&
          list.length === 0 &&
          error && <EmptyState type='error' text='Unexpected Error' subText={error} />
        }>
        <Grid container spacing={1} alignContent='center'>
          <Grid item xs={12}>
            {list.length > 0 && (
              <>
                <List>{displayList}</List>
                {loading && list.length > 0 && <DotLoader align='center' />}

                {!loading && !hasMore && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    No more results to display
                  </Typography>
                )}

                {error && list.length > 0 && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    Failed to load campaign groups
                  </Typography>
                )}
              </>
            )}

            {list.length === 0 && error && <EmptyState type='error' text='Unexpected Error' subText={error} />}

            {list.length === 0 && !error && noSearchResults && (
              <EmptyState
                type='no-records-found'
                text='No groups found matching your search criteria'
                subText='Try alternate words or selections.'
              />
            )}

            {list.length === 0 && !error && !noSearchResults && (
              <EmptyState
                type='no-items-1'
                text='Campaign is not assigned to any groups'
                subText='This list will be populated with groups once the campaign is assigned to them.'
              />
            )}
          </Grid>
        </Grid>
      </AsyncLoader>
    </>
  );
};

export default CampaignGroupList;
